import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { TabContent } from "./TabContent";
import { useBuilderContext } from "../../StrategyCreator/context/BuilderContext";
import { useOptionChainContext } from "../../../context/OptionChainContext";
import { useOptionChainScreenContext } from "../context/OptionChainContext";

const DEFAULT_SCRIPS = [
  "NIFTY",
  "BANKNIFTY",
  "FINNIFTY",
  "MIDCPNIFTY",
  "NIFTYNXT50",
  "SENSEX",
];

export function SearchTabContent({
  searchScrip,
  setSearchScrip,
  preSelectedSymbol,
}) {
  const { searchSymbol, setSearchSymbol, setSelectedSymbol } =
    useOptionChainScreenContext();
  const { allSymbols } = useBuilderContext();

  const [inputValue, setInputValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const inputRef = useRef();
  const wrapperRef = useRef();

  // Handle preselected symbol from InstrumentModal when in Search tab
  useEffect(() => {
    if (preSelectedSymbol && allSymbols) {
      const symbolObj = allSymbols.find((s) => s.scrip === preSelectedSymbol);
      if (symbolObj) {
        setSearchSymbol(symbolObj);
        setSelectedSymbol(symbolObj);
        setSearchScrip(preSelectedSymbol);
      }
    }
  }, [
    preSelectedSymbol,
    allSymbols,
    setSearchSymbol,
    setSelectedSymbol,
    setSearchScrip,
  ]);

  useEffect(() => {
    if (searchSymbol) {
      setSelectedSymbol(searchSymbol);
    }
  }, [searchSymbol, setSelectedSymbol]);

  const handleSelect = (scrip) => {
    const symbolObj = allSymbols?.find((s) => s.scrip === scrip);
    if (symbolObj) {
      setSearchSymbol(symbolObj);
      setSelectedSymbol(symbolObj);
      setSearchScrip(scrip);
      setInputValue("");
      setDropdownOpen(false);
      setSearchOpen(false);
    }
  };

  const filteredScrips = allSymbols
    ?.filter((s) => s.scrip.toLowerCase().includes(inputValue.toLowerCase()))
    .map((s) => s.scrip);

  const visibleScrips = inputValue ? filteredScrips : DEFAULT_SCRIPS;

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
      setDropdownOpen(true);
    }
  }, [searchOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Use searchScrip to determine if we should show search or selected content
  const hasSelectedSymbol = searchScrip && searchSymbol;

  return (
    <div className="relative w-full" ref={wrapperRef}>
      {searchOpen || !hasSelectedSymbol ? (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder="Search underlaying instruments, etc..."
          onFocus={() => setDropdownOpen(true)}
          onChange={(e) => setInputValue(e.target.value)}
          className="w-full rounded px-3 py-2 focus:outline-none bg-p400 placeholder:text-[12px] text-p300"
        />
      ) : (
        <TabContent
          symbol={searchSymbol}
          fromSearchTab={true}
          onOpenSearch={() => setSearchOpen(true)}
        />
      )}

      {dropdownOpen && visibleScrips?.length > 0 && (
        <div className="absolute w-full bg-p400 mt-1 rounded shadow-md max-h-[200px] overflow-y-auto z-20 no-scrollbar">
          {visibleScrips.map((scrip) => (
            <div
              key={scrip}
              onClick={() => handleSelect(scrip)}
              className="px-4 py-2 hover:bg-n100 bg-p400 text-p300 cursor-pointer text-[12px] text-gray-700 border-b">
              {scrip}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
